import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { convertCurrency } from "../helper/CurrencyConverter";
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("authToken");

const WishListSection = ({ selectedCurrency }) => {
  const [favorites, setFavorites] = useState([]);
  const [cart, setCart] = useState([]);

  //toast 
  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    showConfirmButton: false,
    timer: 2000,
  });

  // Merge helper to avoid duplicates
  const mergeFavorites = (localFavs, backendFavs) => {
    const map = {};
    [...localFavs, ...backendFavs].forEach(item => {
      map[item.id] = item; // overwrite duplicates
    });
    return Object.values(map);
  };

  // Load favorites and cart from localStorage & backend
  useEffect(() => {
    const storedFavs = JSON.parse(localStorage.getItem("favorites")) || [];
    setFavorites(storedFavs);

    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(storedCart);

    const fetchAndSyncFavorites = async () => {
      if (!token) return;

      try {
        // Fetch all favorites from backend
        const res = await axios.get(`${API_URL}/wishlist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const backendFavs = res.data.favorites || [];

        // Merge backend + localStorage
        const mergedFavs = mergeFavorites(storedFavs, backendFavs);
        setFavorites(mergedFavs);
        localStorage.setItem("favorites", JSON.stringify(mergedFavs));

        // Sync merged favorites to backend
        await axios.post(
          `${API_URL}/wishlist/sync`,
          { favorites: mergedFavs },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error("Error syncing favorites:", err);
      }
    };

    fetchAndSyncFavorites();
  }, []);

  // Remove item from favorites
  const removeFavorite = async (id) => {
    // Remove from local state immediately
    const updatedFavs = favorites.filter((item) => item.id !== id);
    setFavorites(updatedFavs);

    // Update localStorage immediately
    localStorage.setItem("favorites", JSON.stringify(updatedFavs));

    // Remove from backend if user is logged in
    if (token) {
      try {
        const res = await axios.post(
          `${API_URL}/wishlist/remove`,
          { productId: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Update state & localStorage with backend response
        const backendFavs = res.data.favorites
          .filter(f => f.product && f.product._id) // ensure valid products
          .map(f => ({
            id: f.product._id,
            name: f.name,
            price: f.price,
            images: f.product.images,
          }));

        setFavorites(backendFavs);
        localStorage.setItem("favorites", JSON.stringify(backendFavs));
      } catch (err) {
        console.error("Remove Favorite Error:", err);
      }
    }
  };


  // Add favorite item to cart
  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product.id);
    let newCart;

    if (existing) {
      newCart = cart.map((item) =>
        item.id === product.id
          ? { ...item, qty: item.qty + 1 }
          : item
      );
    } else {
      newCart = [
        ...cart,
        { id: product.id, name: product.name, price: product.price, qty: 1, images: product.images},
      ];
    }

    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    Toast.fire({
      icon: "success",
      title: `${product.name} added to wishlist!`,
    });
  };

  if (favorites.length === 0) {
    return (
      <section className="cart py-80">
        <div className="container container-lg text-center">
          <h3 className="mb-16">Your Wishlist is empty </h3>
          <Link to="/shop" className="btn btn-main rounded-pill px-48">
            Continue Shopping
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="cart py-80">
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-lg-11">
            <div className="cart-table border border-gray-100 rounded-8">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <table className="table rounded-8 overflow-hidden">
                  <thead>
                    <tr className="border-bottom border-neutral-100">
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">
                        Delete
                      </th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">
                        Product Name
                      </th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">
                        Unit Price
                      </th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32" />
                    </tr>
                  </thead>
                  <tbody>
                    {favorites.map((item) => (
                      <tr key={item.id}>
                        <td className="px-40 py-32 border-end border-neutral-100">
                          <button
                            type="button"
                            className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                            onClick={() => removeFavorite(item.id)}
                          >
                            <i className="ph ph-x-circle text-2xl d-flex" />
                            Remove
                          </button>
                        </td>
                        <td className="px-40 py-32 border-end border-neutral-100">
                          <div className="table-product d-flex align-items-center gap-24">
                            <Link
                              to={`/product-details/${item.id}`}
                              className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                            >
                              <img
                                src={item.images[0] || "assets/images/thumbs/product-details-thumb1.png"}
                                alt={item.name}
                              />
                            </Link>
                            <div className="table-product__content text-start">
                              <h6 className="title text-lg fw-semibold mb-8">
                                <Link
                                  to={`/product-details/${item.id}`}
                                  className="link text-line-2"
                                  tabIndex={0}
                                >
                                  {item.name}
                                </Link>
                              </h6>
                            </div>
                          </div>
                        </td>
                        <td className="px-40 py-32 border-end border-neutral-100">
                          <span className="text-heading text-md fw-semibold">
                            {selectedCurrency} {convertCurrency(item.price, selectedCurrency).toFixed(2)}{" "}
                          </span>
                        </td>
                        <td className="px-40 py-32">
                          <button
                            className="btn btn-main rounded-8 px-64"
                            onClick={() => addToCart(item)}
                          >
                            Add To Cart <i className="ph ph-shopping-cart" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WishListSection;
